import React from 'react';

export function AutolyseTechnique() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Autolyse Technique</h3>
        <p className="mb-4">
          Autolyse is a technique where flour and water are mixed together and allowed to rest before adding other ingredients like salt and yeast/starter. This simple step can dramatically improve your bread's texture, flavor, and overall quality.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">The Science Behind Autolyse</h3>
        
        <div className="space-y-4">
          <p className="text-white/80">
            During autolyse, several important processes occur that benefit your dough:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Enzyme Activity</h4>
              <p className="text-sm text-white/80">
                Enzymes naturally present in flour (amylases and proteases) begin breaking down complex starches into simpler sugars and proteins into amino acids. This makes more food available for yeast fermentation and contributes to better flavor development.
              </p>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Gluten Development</h4>
              <p className="text-sm text-white/80">
                Gluten proteins (glutenin and gliadin) absorb water and begin forming bonds naturally, without mechanical kneading. This passive gluten development creates a more extensible (stretchy) dough with better structure.
              </p>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Flour Hydration</h4>
              <p className="text-sm text-white/80">
                Flour particles fully absorb water, ensuring even hydration throughout the dough. This eliminates dry spots and creates a smoother, more cohesive dough that's easier to work with.
              </p>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Reduced Kneading</h4>
              <p className="text-sm text-white/80">
                The natural gluten development during autolyse means less mechanical kneading is required. This preserves more of the delicate gluten structure and results in better gas retention and a more open crumb.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Basic Autolyse Method</h3>
        
        <div className="space-y-4">
          <ol className="list-decimal list-inside text-sm text-white/80 space-y-3">
            <li className="p-2 bg-white/5 rounded-lg">
              <span className="font-medium text-white">Mix flour and water only</span>
              <p className="mt-1 ml-6">Combine all the flour and water from your recipe (hold back salt, starter/yeast, and any other ingredients). Mix just until no dry flour remains—don't knead.</p>
            </li>
            
            <li className="p-2 bg-white/5 rounded-lg">
              <span className="font-medium text-white">Cover and rest</span>
              <p className="mt-1 ml-6">Cover the dough with a damp cloth or plastic wrap and let it rest at room temperature.</p>
            </li>
            
            <li className="p-2 bg-white/5 rounded-lg">
              <span className="font-medium text-white">Rest duration</span>
              <p className="mt-1 ml-6">Rest for 20-60 minutes (longer for whole grain flours, shorter for white flours).</p>
            </li>
            
            <li className="p-2 bg-white/5 rounded-lg">
              <span className="font-medium text-white">Add remaining ingredients</span>
              <p className="mt-1 ml-6">After the rest period, add salt, starter/yeast, and any other ingredients. Incorporate them thoroughly into the dough.</p>
            </li>
            
            <li className="p-2 bg-white/5 rounded-lg">
              <span className="font-medium text-white">Continue with recipe</span>
              <p className="mt-1 ml-6">Proceed with your normal kneading, folding, and fermentation process.</p>
            </li>
          </ol>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Variations and Advanced Techniques</h3>
        
        <div className="space-y-4">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Including Starter in Autolyse</h4>
            <p className="text-sm text-white/80 mt-1">
              Some bakers include sourdough starter in the autolyse (but still hold back salt). This can be beneficial when:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Using very long autolyse periods (2+ hours)</li>
              <li>Working with whole grain flours that benefit from early fermentation</li>
              <li>Dealing with high hydration doughs that are difficult to incorporate starter into later</li>
            </ul>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Extended Autolyse</h4>
            <p className="text-sm text-white/80 mt-1">
              Extended autolyse periods (2-24 hours) can be used for:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Very strong flours that benefit from longer hydration</li>
              <li>Whole grain doughs to soften bran particles</li>
              <li>Developing more complex flavors</li>
            </ul>
            <p className="text-sm text-white/80 mt-2">
              Note: For very long autolyse periods, refrigeration may be necessary to prevent unwanted fermentation from wild yeasts naturally present in flour.
            </p>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Salt-Delayed Autolyse</h4>
            <p className="text-sm text-white/80 mt-1">
              Some recipes call for adding salt after a short initial mix but before the main bulk fermentation. This approach:
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Allows for some enzyme activity without salt inhibition</li>
              <li>Helps control fermentation rate</li>
              <li>Can improve dough strength</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Autolyse Benefits by Flour Type</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/80">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-amber-400">Flour Type</th>
                <th className="text-left py-3 px-4 text-amber-400">Ideal Autolyse Duration</th>
                <th className="text-left py-3 px-4 text-amber-400">Benefits</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">White Bread Flour</td>
                <td className="py-3 px-4">20-45 minutes</td>
                <td className="py-3 px-4">Improved extensibility, reduced mixing time, better volume</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Whole Wheat Flour</td>
                <td className="py-3 px-4">45-90 minutes</td>
                <td className="py-3 px-4">Softened bran, better hydration, improved structure</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Rye Flour</td>
                <td className="py-3 px-4">20-30 minutes</td>
                <td className="py-3 px-4">Improved mixing, better pentosan hydration</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Ancient Grains (Spelt, Einkorn)</td>
                <td className="py-3 px-4">60-120 minutes</td>
                <td className="py-3 px-4">Enhanced flavor, improved weak gluten structure</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">High-Protein Flour</td>
                <td className="py-3 px-4">30-60 minutes</td>
                <td className="py-3 px-4">Reduced mixing damage, better extensibility</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Troubleshooting Autolyse</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Dough Too Sticky After Autolyse</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Add salt immediately after autolyse (salt helps tighten the dough)</li>
              <li>Use wet hands rather than floured hands when handling</li>
              <li>Perform a series of stretch and folds to build strength</li>
              <li>Consider reducing hydration slightly in future batches</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Difficulty Incorporating Ingredients After Autolyse</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Dissolve salt in a small amount of water before adding</li>
              <li>Use the pinching method to incorporate starter or yeast</li>
              <li>Consider a shorter autolyse period next time</li>
              <li>For very stiff doughs, allow to rest 10 minutes after adding ingredients before mixing</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Dough Starts Fermenting During Autolyse</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Use cooler water for the initial mix</li>
              <li>Shorten the autolyse period</li>
              <li>Consider refrigerating for long autolyse periods</li>
              <li>This is normal with freshly milled flour which contains more natural yeasts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}